import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Upload, Power, Megaphone } from "lucide-react";

export default function PdfAdsAdminPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ brand_name: "", title: "", description: "", contact: "", image_url: "", valid_till: "", active: true });
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();

  const load = () => api.get("/admin/pdf-ads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("बैनर अपलोड हो गया");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.brand_name.trim()) { toast.error("कंपनी / ब्रांड का नाम दें"); return; }
    try {
      await api.post("/admin/pdf-ads", form);
      toast.success("विज्ञापन जोड़ा गया");
      setForm({ brand_name: "", title: "", description: "", contact: "", image_url: "", valid_till: "", active: true });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const toggle = async (id, active) => { await api.patch(`/admin/pdf-ads/${id}`, { active }); load(); };
  const del = async (id) => { if (!window.confirm("हटाएँ?")) return; await api.delete(`/admin/pdf-ads/${id}`); load(); };

  return (
    <div data-testid="admin-pdf-ads-page">
      <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3 mb-2"><Megaphone className="w-6 h-6 text-[#EA580C]"/>e-Paper प्रचार / विज्ञापन</h1>
      <p className="text-sm text-slate-500 mb-6">कंपनी / ब्रांड का विज्ञापन जो रोज़ के e-Paper PDF में छपेगा</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-bold text-[#0F172A] mb-3">नया विज्ञापन जोड़ें</h3>
          <div className="space-y-3">
            <Input placeholder="कंपनी / ब्रांड नाम *" value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })} data-testid="ad-brand"/>
            <Input placeholder="विज्ञापन शीर्षक (जैसे: 50% छूट)" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ad-title"/>
            <Textarea rows={3} maxLength={400} placeholder="विवरण / ऑफ़र" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ad-desc"/>
            <Input placeholder="संपर्क / फ़ोन (वैकल्पिक)" value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="ad-contact"/>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-slate-300 hover:border-[#EA580C] text-sm">
                <Upload className="w-4 h-4"/>{uploading ? "अपलोड…" : "बैनर / लोगो"}
                <input ref={photoRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="ad-img-input"/>
              </label>
              {form.image_url && <img src={form.image_url} alt="" className="h-16 object-contain"/>}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold block mb-1">वैध तक (वैकल्पिक)</label>
              <Input type="date" value={form.valid_till} onChange={(e) => setForm({ ...form, valid_till: e.target.value })}/>
            </div>
            <Button onClick={submit} className="w-full bg-[#EA580C] hover:bg-[#DC2626] text-white" data-testid="ad-submit"><Megaphone className="w-4 h-4 mr-2"/>विज्ञापन जोड़ें</Button>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-[#0F172A] mb-3">विज्ञापन ({items.filter(x => x.active).length} सक्रिय)</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {items.map((a) => (
              <div key={a.id} className={`bg-white border rounded-lg p-3 ${a.active ? "border-orange-300" : "border-slate-200 opacity-50"}`} data-testid={`ad-${a.id}`}>
                <div className="flex items-start gap-3">
                  {a.image_url && <img src={a.image_url} alt="" className="w-14 h-14 rounded object-contain shrink-0 bg-slate-50"/>}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0F172A]">{a.brand_name}</div>
                    {a.title && <div className="text-xs text-[#EA580C] font-bold">{a.title}</div>}
                    {a.description && <div className="text-xs text-slate-600 line-clamp-2 mt-1">{a.description}</div>}
                    {a.contact && <div className="text-[10px] text-slate-500 mt-1">📞 {a.contact}</div>}
                    {a.valid_till && <div className="text-[10px] text-slate-400">वैध तक: {a.valid_till}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => toggle(a.id, !a.active)} className={`p-1 rounded ${a.active ? "text-emerald-600" : "text-slate-400"}`} data-testid={`ad-toggle-${a.id}`}><Power className="w-3.5 h-3.5"/></button>
                    <button onClick={() => del(a.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`ad-del-${a.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
